import { FieldType } from 'module/api/field/types';
import { FieldDataType } from 'module/api/field/types';
import { Field } from 'module/api/field/types';
import { SimpleSearchFilter } from 'module/api/filter/types';
import { Filter } from 'module/types/filter-field/filter';
import { IntegrationConfig, IntegrationSource } from '../types';

export type IntegrateFieldOption = {
  color?: string;
  label: string;
  valueStr: string;
  active?: boolean;
  integrationFieldId?: string;
  valueMeta?: any;
};

export interface IntegrationField
  extends Omit<Field, 'fieldId' | 'metaData' | 'fieldType'> {
  parentFieldName: string;
  parentFieldId?: string;
  integrationFieldId: string;
  allowMultipleMapping?: boolean;
  fieldType: FieldType;
  type?: FieldType;
  required?: boolean;
  fieldValue?: string;
  editableByFieldMapping?: boolean;
  metaData?: {
    options?: IntegrateFieldOption[];
    rangeAvailable?: boolean;
    range?: {
      min?: number;
      max?: number;
    };
    default?: any;
  };
}

/**
 * Utility type that requires at least the specified keys from T
 */
type AtLeast<T, K extends keyof T> = Partial<T> & Pick<T, K>;

export enum UserStatus {
  Active = 'ACTIVE',
  Inactive = 'INACTIVE',
  Invited = 'INVITED',
  Passive = 'PASSIVE',
}

export enum IntegrationStatusTypes {
  Expired = 'AUTHENTICATION_EXPIRED',
}

export interface EmailUser {
  userId?: number;
  emailId?: string;
}

export interface OptionTypeBase {
  [key: string]: any;
}

export type SalesforceIntegrationId = { integrationId: string | 'new' };

export type SalesforceIntegrationProvider = {
  authType: string;
  oauthUrl: string;
  oauthSandboxUrl: string;
  oauthReauthUrl: string;
  marketplaceAppUrl: string;
  marketplaceAppInstallationRequired?: boolean;
};

export enum SalesforceActions {
  Project = 'scarletProject',
  Task = 'task',
  Account = 'company',
  Contact = 'user',
}

export type SalesforceObject = {
  id: string;
  name: string;
  url?: string;
  type?: string;
};

export type SalesforceResourceTypesByCategory = {
  project?: SalesforceObject[];
  company?: SalesforceObject[];
  user?: SalesforceObject[];
};

export enum AutomationOperation {
  Upsert = 'UPSERT',
  UpsertRelatedRecords = 'UPSERT_RELATED_RECORDS',
}

export type Condition = {
  name: string;
  value: string;
  operation: string;
};

export type SalesforceAutomationCondition = Omit<
  SimpleSearchFilter,
  'customFields'
>;

export type SalesforceAutomationAction = {
  integrationType: string;
  resourceType: SalesforceActions;
  operation: AutomationOperation;
  inputs: Record<string, unknown>;
  dependentActions?: SalesforceAutomationAction[];
  sourceParentResourceType?: string;
  generateAutomationForFutureInserts?: boolean;
  sourceParentResourceId?: string;
  sourceParentResourceFieldId?: string;
  sourceResourceType?: string;
  sourceRelationshipName?: string;
};

export type SalesforceOrderPayload = {
  automationId: number;
  automationUpdatedOrder: number;
};

export type SalesforceAutomation = {
  actions: SalesforceAutomationAction[];
  automationId?: number;
  automationName?: string;
  description?: string;
  createdAt: string;
  order: number;
  integrationType: string;
  enabled: boolean;
  generateAutomationForFutureInserts?: boolean;
  matchingCondition?: SalesforceAutomationCondition;
  object?: string;
  triggerCondition: SalesforceAutomationCondition;
  source: IntegrationSource;
  sourceId: number;
  updatedAt: string;
  automationType?: 'CRM' | 'CRM_V2';
};

export type CreateAutomationPayload = Omit<
  SalesforceAutomation,
  'automationId' | 'createdAt' | 'deleted' | 'order' | 'updatedAt'
>;

export type UpdateAutomationPayload = Omit<
  SalesforceAutomation,
  'createdAt' | 'deleted' | 'order' | 'updatedAt'
>;

export type SalesforceSettingsMeta = {
  externalAccountId: string;
  errorEmailIntervalSeconds?: number;
  errorEmailEnabled?: boolean;
  errorEmailRecipients?: EmailUser[];
};

export type SalesforceConfig = IntegrationConfig<SalesforceSettingsMeta>;

export enum SalesforceSyncTypes {
  SyncTwoWay = 'SYNC_TWO_WAY',
  SyncNativeToExternal = 'SYNC_NATIVE_TO_EXTERNAL',
  SyncExternalToNative = 'SYNC_EXTERNAL_TO_NATIVE',
  None = 'NONE',
}

export interface SalesforceSyncOption {
  label: SalesforceSyncTypes;
  value: SalesforceSyncTypes;
}

export interface SalesforceObjectOption {
  label: string;
  value: string;
}

export interface SalesforceAutomationFieldValue {
  label?: string;
  value?: string | number;
  valueStr?: string;
  color?: string;
  valueMeta?: {
    description?: string;
    fieldLabel?: string;
    parentFieldName?: string;
    pillText?: string;
    sourceParentResourceFieldId?: string;
    sourceRelationshipName?: string;
    sourceResourceType?: string;
    type?: string;
  };
}

export interface SalesforceMappingOption<Extra = unknown> {
  label: string;
  value: string | number;
  extra?: Extra;
  name?: string;
  parent?: string;
  valueStr?: string;
}

export type SalesforceFieldMapping = {
  fieldMappingConfigId?: number;
  integrationId: number;
  externalIntegrationType: 'salesforce';
  resouceMappingConfigId: number;
  nativeFieldId: number;
  nativeFieldName?: string;
  nativeFieldLabel: string;
  externalFieldId: string;
  externalFieldName: string;
  syncType: SalesforceSyncTypes;
  enabled?: boolean;
  allowedSyncTypes: [SalesforceSyncTypes];
};

export type SalesforceResourceMapping = {
  resourceMappingConfigId: number;
  integrationId: number;
  externalIntegrationType: 'salesforce';
  nativeResourceType: string;
  externalResourceType: string;
  syncType: SalesforceSyncTypes;
  fieldMappings: SalesforceFieldMapping[];
  enabled?: boolean;
};

export type SalesforceResourceCreatePayload = Pick<
  SalesforceResourceMapping,
  | 'externalIntegrationType'
  | 'externalResourceType'
  | 'nativeResourceType'
  | 'syncType'
  | 'enabled'
>;

export enum AutomationErrorCodes {
  ConflictingFieldValue = 'CONFLICTING_FIELD_VALUE',
}

export type AutomationError = {
  code: AutomationErrorCodes;
  reason: string;
};

export interface SfInstallations {
  installationId: number;
  externalInstallationId: string;
  externalIntegrationType: string;
  externalAccountId: string;
  installedAppId: string;
  installedAppName: string;
  installedAppVersionId: string;
  installedAppVersionName: string;
  status: string;
}

export type RLProjectFieldMapping = Record<
  string,
  SalesforceAutomationFieldValue | null
>;

export interface SalesforceAutomationLocalData extends SalesforceAutomation {
  salesforceObject?: SalesforceObjectOption | null;
  triggers?: Filter;
  matches?: Filter;
  rocketlaneFields?: RLProjectFieldMapping;
  salesForceType?: SalesforceActions;
  accountFields?: RLProjectFieldMapping;
  contactFields?: RLProjectFieldMapping;
  sourceResourceType?: string;
  sourceRelationshipName?: string;
  sourceParentResourceId?: string;
  sourceParentResourceType?: string;
  sourceParentResourceFieldId?: string;
  actions: SalesforceAutomationAction[];
}

export type PartialSalesforceAutomationLocalData = Partial<
  Pick<
    SalesforceAutomationLocalData,
    | 'automationName'
    | 'description'
    | 'salesforceObject'
    | 'triggers'
    | 'matches'
    | 'rocketlaneFields'
    | 'automationId'
    | 'salesForceType'
    | 'accountFields'
    | 'contactFields'
    | 'sourceResourceType'
    | 'sourceRelationshipName'
    | 'sourceParentResourceId'
    | 'sourceParentResourceType'
    | 'sourceParentResourceFieldId'
    | 'generateAutomationForFutureInserts'
    | 'actions'
  >
>;

export type NativeFieldExtra = {
  isEditable: boolean;
  fieldName: string;
  fieldType: FieldType;
  fieldDataType: FieldDataType;
  optionsAvailable?: boolean;
};

export interface SalesforceFieldMappingLocalData
  extends SalesforceFieldMapping {
  id: string;
  salesforceField: SalesforceMappingOption<boolean> | null;
  syncField: SalesforceSyncTypes;
  rocketlaneField: SalesforceMappingOption<NativeFieldExtra> | null;
}

export interface SalesforceResourceMappingLocalData
  extends Omit<SalesforceResourceMapping, 'fieldMappings'> {
  id: string;
  fieldMappings: SalesforceFieldMappingLocalData[];
}

export type PartialSalesforceFieldMappingLocalData = AtLeast<
  SalesforceFieldMappingLocalData,
  'id' | 'integrationId' | 'resouceMappingConfigId'
>;

export type BySalesforceResourceMap = Record<
  string,
  SalesforceResourceMappingLocalData
>;

/* Project specific types */

export enum ProjectFinancialsContractType {
  FIXED = 'FIXED_FEE',
  TM = 'TIME_AND_MATERIAL',
  SUBSCRIPTION = 'SUBSCRIPTION',
  NONBILLABLE = 'NON_BILLABLE',
}

export enum ConditionTypes {
  Triggers = 'triggers',
  Matches = 'matches',
}

export type CreateProps = {
  integrationId: number;
  defaultValue?: SalesforceAutomationCondition;
  automationDetails?: PartialSalesforceAutomationLocalData;
  onChange: (val: PartialSalesforceAutomationLocalData) => void;
};

export type StatusTypes = 'idle' | 'error' | 'pending' | 'success';

/* Error Logs Types */

export type ErrorLogs = {
  requestId: string;
  logTimestamp: string;
  logMessage: string;
  entityName: string;
  tags: {
    externalResourceId?: string;
    externalResourceType?: string;
    nativeResourceId?: string;
    nativeResourceType?: string;
    nativeResourceName?: string;
  };
};

export type ErrorLogsResponse = {
  data: ErrorLogs[];
  total: number;
  page: number;
  limit: number;
};

/* Child Resources Types */

export type ChildResource = {
  sourceResourceType: string;
  sourceRelationshipName: string;
  sourceParentResourceFieldId?: string;
};

export type ChildResourcesResponse = {
  sourceParentResourceId: string;
  sourceParentResourceType: string;
  salesforceRelatedResources: ChildResource[];
};

/* Contact Field Sync Configuration Types */

export type ContactMandatoryField = {
  salesforceFieldId: string;
  salesforceFieldName: string;
  salesforceFieldLabel: string;
  rocketlaneFieldId?: string;
  rocketlaneFieldName?: string;
  rocketlaneFieldLabel?: string;
  isMapped: boolean;
  isRequired: boolean;
  fieldType?: string;
  fieldDataType?: string;
};

export type ContactFieldSyncConfig = {
  contactObject: string;
  mandatoryFields: ContactMandatoryField[];
  isSyncConfigured: boolean;
  missingMappingsCount: number;
};

/* Contact Field Sync Detail Configuration (for Modal) */
export type ContactFieldMappingDetail = {
  fieldMappingConfigId?: number; // For update operations
  integrationId?: number;
  externalIntegrationType?: string;
  nativeResourceType?: string;
  externalResourceType?: string;
  resouceMappingConfigId?: number; // Note: API has typo, keeping it for compatibility
  nativeFieldName?: string;
  nativeFieldLabel?: string;
  externalFieldName?: string | null;
  externalFieldLabel?: string | null;
  externalFieldId?: string | null; // For mapping to Salesforce field
  syncType?: string;
  allowedSyncTypes?: string[]; // Available sync directions for this field
  fieldType?: string; // Salesforce field type
  fieldDataType?: string; // Salesforce field data type
  enabled?: boolean;
  deleted?: boolean;
};

export type ContactFieldSyncDetailConfig = {
  resourceMappingConfigId?: number; // For update operations
  integrationId?: number;
  externalIntegrationType?: string;
  nativeResourceType?: string;
  externalResourceType?: string;
  syncType?: string;
  fieldMappings?: ContactFieldMappingDetail[];
  enabled?: boolean;
  deleted?: boolean;
};

/* Mappable Salesforce Fields for Contact Field Sync */
export type MappableSalesforceField = {
  integrationFieldId: string;
  fieldName: string;
  fieldLabel: string;
  fieldDataType?: string;
  fieldType?: string;
  isEnabled?: boolean;
  required?: boolean;
  editableByFieldMapping?: boolean;
  parentFieldName?: string;
  externalChildResourceType?: string;
  externalChildFieldId?: string;
  externalParentFieldId?: string;
};

export type MappableSalesforceFieldsResponse = MappableSalesforceField[];

/* Save/Update Contact Field Sync Configuration */
export type SaveContactFieldMappingPayload = {
  fieldMappingConfigId?: number | null;
  integrationId: number;
  externalIntegrationType: string;
  resouceMappingConfigId?: number | null;
  nativeFieldName: string;
  nativeFieldLabel: string;
  externalFieldId: string;
  externalFieldName: string;
  syncType: string;
  allowedSyncTypes?: string[];
  externalChildResourceType?: string;
  externalChildFieldId?: string;
  externalParentFieldId?: string;
  enabled: boolean;
  deleted: boolean;
};

export type SaveContactFieldSyncPayload = {
  resourceMappingConfigId?: number | null;
  integrationId: number;
  externalIntegrationType: string;
  nativeResourceType: string;
  externalResourceType: string;
  syncType: string;
  fieldMappings: SaveContactFieldMappingPayload[];
  enabled: boolean;
  deleted: boolean;
};

export type SaveContactFieldSyncResponse = ContactFieldSyncDetailConfig;
